import { z } from "zod";

const optional = () => z.string().optional().or(z.literal(""));

export const companyFormSchema = z.object({
  name: z.string().trim().min(1, "Company name is required").max(255),
  gstin: optional(),
  pan_no: optional(),
  cin_no: optional(),
  phone: optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  website: optional(),
  addr1: optional(),
  addr2: optional(),
  city: optional(),
  state: optional(),
  country: z.string().trim().min(1),
  pincode: optional(),
  client_prefix: z.string().trim().min(1).max(8),
  vendor_prefix: z.string().trim().min(1).max(8),
  po_prefix: z.string().trim().min(1).max(8),
  invoice_prefix: z.string().trim().min(1).max(8),
  quotation_prefix: z.string().trim().min(1).max(8),
  so_prefix: z.string().trim().min(1).max(8),
  credit_note_prefix: z.string().trim().min(1).max(8),
  plan_prefix: z.string().trim().min(1).max(8),
  dispatch_prefix: z.string().trim().min(1).max(8),
  bank_name: optional(),
  bank_account: optional(),
  bank_ifsc: optional(),
});

export type CompanyFormValues = z.infer<typeof companyFormSchema>;

export const emptyCompanyForm: CompanyFormValues = {
  name: "",
  gstin: "",
  pan_no: "",
  cin_no: "",
  phone: "",
  email: "",
  website: "",
  addr1: "",
  addr2: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
  client_prefix: "CL",
  vendor_prefix: "VN",
  po_prefix: "PO",
  invoice_prefix: "INV",
  quotation_prefix: "QT",
  so_prefix: "SO",
  credit_note_prefix: "CN",
  plan_prefix: "PLAN",
  dispatch_prefix: "DN",
  bank_name: "",
  bank_account: "",
  bank_ifsc: "",
};

export const defaultsFormSchema = z.object({
  default_payment_terms: optional(),
  default_gst_slab: z.coerce.number().min(0).max(100),
  fiscal_year_start_month: z.coerce.number().int().min(1).max(12),
  default_location_id: optional(),
  mill_kerf_mm: z.coerce.number().min(0),
  mill_edge_trim_mm: z.coerce.number().min(0),
  mill_weld_allowance_mm: z.coerce.number().min(0),
  mill_tube_weight_factor: z.coerce.number().min(0),
  mill_ctl_blade_mm: z.coerce.number().min(0),
  mill_ctl_yield_margin_pct: z.coerce.number().min(0).max(100),
  mill_skinpass_weight_factor: z.coerce.number().min(0).max(1),
  mill_inbound_default_process_type_id: optional(),
});

export type DefaultsFormValues = z.infer<typeof defaultsFormSchema>;

export const emptyDefaultsForm: DefaultsFormValues = {
  default_payment_terms: "",
  default_gst_slab: 18,
  fiscal_year_start_month: 4,
  default_location_id: "",
  mill_kerf_mm: 3,
  mill_edge_trim_mm: 10,
  mill_weld_allowance_mm: 1,
  mill_tube_weight_factor: 0.0246615,
  mill_ctl_blade_mm: 3,
  mill_ctl_yield_margin_pct: 5,
  mill_skinpass_weight_factor: 0.999,
  mill_inbound_default_process_type_id: "",
};

export const taxFormSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(100),
    tax_code: z
      .string()
      .trim()
      .min(1, "Code is required")
      .max(20)
      .regex(/^[A-Za-z0-9._-]+$/, "Use letters, numbers, . - or _"),
    rate: z.coerce.number().min(0).max(100),
    tax_type: z.enum(["gst", "cgst", "sgst", "igst", "cess", "other"]),
    gst_slab: z.coerce.number().min(0).max(100).nullable().optional(),
    effective_from: z.string().min(1, "Effective from is required"),
    effective_to: optional(),
    is_active: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.effective_to?.trim() && values.effective_to < values.effective_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["effective_to"],
        message: "Must be on or after effective from",
      });
    }
    if (
      (values.tax_type === "cgst" || values.tax_type === "sgst" || values.tax_type === "igst") &&
      (values.gst_slab === null || values.gst_slab === undefined || Number.isNaN(Number(values.gst_slab)))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["gst_slab"],
        message: "Set the GST slab % (e.g. 18) for CGST / SGST / IGST",
      });
    }
  });

export type TaxFormValues = z.infer<typeof taxFormSchema>;

export const emptyTaxForm: TaxFormValues = {
  name: "",
  tax_code: "",
  rate: 9,
  tax_type: "cgst",
  gst_slab: 18,
  effective_from: "2026-04-01",
  effective_to: "",
  is_active: true,
};
